import { AnchorCalculator, ShapeGeoBuilder } from '@jxUml';

/**
 * Straightens a connector's chain of 2+ waypoints in place — keeps existing
 * manual anchors, corrects every segment to be purely horizontal or vertical.
 * Picks the correct border side per end (based on shape-center direction) and
 * keeps the existing along-border coordinate from that end, rather than
 * re-clamping it here: clamping happens once, in the render pipeline
 * (AnchorCalculator.toShape), against the shape's own bounds. Re-clamping
 * here with different bounds can pin an anchor into a shape's corner when
 * the two shapes don't overlap on that axis.
 * @param {object} connector
 * @param {object} fromShape
 * @param {object} toShape
 */
export function multiWaypointChainStrategy(connector, fromShape, toShape) {
    const waypoints = connector.points || [];
    if (waypoints.length < 2) return;

    const fromH = ShapeGeoBuilder.effectiveHeight(fromShape);
    const toH = ShapeGeoBuilder.effectiveHeight(toShape);

    // Manual anchor: use its current world position. Auto (null): fall back to
    // that shape's center — there's no existing manual anchor position to
    // preserve, and pickBorder/alignPair below only need a reasonable starting
    // point, not the exact auto-computed edge point.
    const existingFrom = connector.fromAnchorPoint
        ? AnchorCalculator.toShape(fromShape, connector.fromAnchorPoint)
        : { x: fromShape.x + fromShape.w / 2, y: fromShape.y + fromH / 2 };
    const existingTo = connector.toAnchorPoint
        ? AnchorCalculator.toShape(toShape, connector.toAnchorPoint)
        : { x: toShape.x + toShape.w / 2, y: toShape.y + toH / 2 };

    const firstWaypoint = waypoints[0];
    const lastWaypoint = waypoints[waypoints.length - 1];

    const startDy = firstWaypoint.y - (fromShape.y + fromH / 2);
    const startDx = firstWaypoint.x - (fromShape.x + fromShape.w / 2);
    const startIsVertical = Math.abs(startDy) >= Math.abs(startDx);
    const start = pickBorder(fromShape, fromH, startDx, startDy, startIsVertical, existingFrom);

    const endDy = lastWaypoint.y - (toShape.y + toH / 2);
    const endDx = lastWaypoint.x - (toShape.x + toShape.w / 2);
    const endIsVertical = Math.abs(endDy) >= Math.abs(endDx);
    const end = pickBorder(toShape, toH, endDx, endDy, endIsVertical, existingTo);

    const chain = [start, ...waypoints.map((p) => ({ x: p.x, y: p.y })), end];

    for (let i = 0; i < chain.length - 1; i++) {
        alignPair(chain[i], chain[i + 1]);
    }

    connector.fromAnchorPoint = { x: chain[0].x - fromShape.x, y: chain[0].y - fromShape.y };
    connector.toAnchorPoint = { x: chain[chain.length - 1].x - toShape.x, y: chain[chain.length - 1].y - toShape.y };
    for (let i = 0; i < waypoints.length; i++) {
        waypoints[i].x = chain[i + 1].x;
        waypoints[i].y = chain[i + 1].y;
    }
}

/**
 * Picks the correct border side on `shape` (top/bottom if isVerticalDominant,
 * else left/right) based on the direction toward the other reference point
 * (dx/dy), keeping the along-border coordinate from `existingPoint` (already
 * clamped to this shape by the caller) unchanged. No further clamp here —
 * that's the render pipeline's job (AnchorCalculator.toShape, used at draw time).
 * @param {object} shape
 * @param {number} effectiveH
 * @param {number} dx - direction toward the other reference point
 * @param {number} dy - direction toward the other reference point
 * @param {boolean} isVerticalDominant
 * @param {{x:number,y:number}} existingPoint
 * @returns {{x:number,y:number}}
 */
function pickBorder(shape, effectiveH, dx, dy, isVerticalDominant, existingPoint) {
    if (isVerticalDominant) {
        return { x: existingPoint.x, y: dy > 0 ? shape.y + effectiveH : shape.y };
    }
    return { x: dx > 0 ? shape.x + shape.w : shape.x, y: existingPoint.y };
}

/**
 * Mutates `b` so the segment a->b is purely horizontal or vertical.
 * @param {{x:number,y:number}} a
 * @param {{x:number,y:number}} b
 */
function alignPair(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const isVerticalDominant = Math.abs(dy) >= Math.abs(dx);
    if (isVerticalDominant) {
        b.x = a.x;
    } else {
        b.y = a.y;
    }
}
